import { handleApi } from "../error";
import { CancelablePromise as Promise } from "../cancelablePromise";
import { hashToArray, filterHash, extend } from "../vanilla";
import { UserService } from "../users";
import { DriveStore } from "../../store/reducers";
import { CacheActions } from "../../store/cache/actions";
import { ICache } from "../../store/cache/types";
import { Device } from "../../model/Device";
import { DeviceLink } from "../../model/DeviceLink";
import { ITrailer } from "../../model/Trailer";
import { TrailerAttachment } from "../../model/TrailerAttachment";
import { IDefect } from "../../model/Defect";
import { error, Source } from "../logger";
import { usesTrailerDeviceIdentity } from "../../sync/trailerAttachmentDeviceLink";

export class BaseCache<T extends IIdEntity> {
    // Resolved per access so a cache can be backed by a different entity type depending on a
    // feature flag that loads after the cache is constructed (see makeTrailerCache).
    private readonly resolvePropertyName: () => string;
    private readonly resolveCacheName: () => keyof ICache;
    private readonly resolveCacheProperties: () => (keyof T & string)[];
    private readonly searchRewrite: ((_) => any) | undefined;
    private readonly getCacheKey: ((_) => string) | undefined;
    private readonly store: DriveStore;
    private userService: UserService;

    constructor(
        store: DriveStore,
        propertyName: string | (() => string),
        localStorageKey: keyof ICache | (() => keyof ICache),
        cacheProperties: (keyof T & string)[] | (() => (keyof T & string)[]),
        searchRewrite?: (entity: any) => any,
        getCacheKey?: (search: any) => string,
        callsPreprocess?: (searches: any, isSearch: boolean) => any[]
    ) {
        this.store = store;
        this.resolvePropertyName = typeof propertyName === "function" ? propertyName : () => propertyName;
        this.resolveCacheName = typeof localStorageKey === "function" ? localStorageKey : () => localStorageKey;
        this.resolveCacheProperties = typeof cacheProperties === "function" ? cacheProperties : () => cacheProperties;
        getCacheKey && (this.getCacheKey = getCacheKey);
        searchRewrite && (this.searchRewrite = searchRewrite);
        callsPreprocess && (this.callsPreprocess = callsPreprocess);
    }

    add(newEntities: Partial<T> | Partial<T>[], userId: string | undefined = undefined) {
        const userid = userId ?? this.getUserId();
        let currentCache = this.getImpl(userid);
        let entities = Array.isArray(newEntities) ? newEntities : [newEntities];

        // Iterate though entries to be cached, and add to the cache
        entities.forEach(entity => {
            if (entity.id) {
                currentCache[entity.id] = this.getSlimEntity(entity);
            }
        });
        // Save the cache back to storage
        this.store.dispatch(CacheActions.addCacheEntity({ cacheType: this.resolveCacheName(), entities: currentCache, userId: userid }));
    }

    remove(newEntities: Partial<T> | Partial<T>[], userId: string | undefined = undefined) {
        const userid = userId ?? this.getUserId();
        let currentCache = this.getImpl(userid);
        let entities = Array.isArray(newEntities) ? newEntities : [newEntities];

        entities.forEach(entity => {
            if (entity.id) {
                delete currentCache[entity.id];
            }
        });
        this.store.dispatch(CacheActions.addCacheEntity({ cacheType: this.resolveCacheName(), entities: currentCache, userId: userid }));
    }

    fetch(api, entitiesOrSearches: any, isSearch?: boolean, userId?: string, resultsLimit?: number) {
        return new Promise<any[]>((resolve, reject) => {
            const userid = userId ?? this.getUserId();
            let currentCache = this.getImpl(userid);
            let calls: any[] = [];
            let searches = Array.isArray(entitiesOrSearches) ? entitiesOrSearches : [entitiesOrSearches];

            calls = this.callsPreprocess(searches, isSearch, resultsLimit);
            if (calls.length > 0) {
                api.multiCall(calls, results => {
                    let slimEntities: any[] = [];

                    if (results && results.length) {
                        results.forEach(result => {
                            result.forEach((entity, i) => {
                                let id = entity.id || (searches[i] && searches[i].id) || null;
                                let search = searches[i];

                                let cacheEntity = this.getSlimEntity(entity);
                                if (Object.keys(cacheEntity).length > 1) {
                                    let cacheKey = this.getCacheKey ? this.getCacheKey(search) : id;
                                    currentCache[cacheKey] = cacheEntity;
                                    slimEntities.push(cacheEntity);
                                }
                            });
                        });
                        // Save the cache back to storage
                        this.store.dispatch(CacheActions.addCacheEntity({ cacheType: this.resolveCacheName(), entities: currentCache, userId: userid }));
                    }
                    resolve(slimEntities);
                }, (errorObject) => {
                    handleApi(errorObject);
                    reject(errorObject);
                });
            } else {
                resolve([]);
            }
        });
    }

    get(searchObj?, userId?: string): T[] {
        // Get old cache from storage (object)
        let currentCache = this.getImpl(userId ?? this.getUserId());

        if (searchObj !== undefined) {
            let cacheKey = this.getCacheKey ? this.getCacheKey(searchObj) : (searchObj && searchObj.id ? searchObj.id : null);
            return cacheKey && currentCache[cacheKey] ? [currentCache[cacheKey]] : filterHash(currentCache, searchObj);
        }
        return hashToArray(currentCache);
    }

    getDict(userId?: string): IIdDictionary<T> {
        return this.getImpl(userId ?? this.getUserId()) || {};
    }

    getOrFetch(api, searches: any, userId?: string) {
        let existing: any[] = [];
        let missing: any[] = [];
        let entities = Array.isArray(searches) ? searches : [searches];

        entities.forEach(entity => {
            let cached = this.get(entity, userId);

            if (cached && cached.length > 0) {
                existing.push(cached);
            } else {
                missing.push(entity);
            }
        });

        if (missing.length > 0) {
            return this.fetch(api, missing, false, userId).then<any[][]>(_1 => entities.map(entity => {
                let e = this.get(entity, userId);
                return e.length > 0 ? e : [];
            }));
        } else {
            return Promise.resolve(existing);
        }
    }

    clear(userId?: string) {
        this.store.dispatch(CacheActions.clearCache({ cacheType: this.resolveCacheName(), userId: userId ?? this.getUserId() }));
    }

    clearAll() {
        this.store.dispatch(CacheActions.clearAllCaches());
    }

    private getUserId(): string {
        if (!this.userService) {
            this.userService = new UserService(this.store);
        }

        const userId = this.userService.getCurrentUser()?.id;
        if (!userId) {
            const errorMessage = `Storage: Unable to get currentUserId when acquiring cache key "${ this.resolveCacheName() }"`;
            error(Source.ERROR, errorMessage);
        }
        return userId ?? "";
    }

    private getImpl(userId: string): IIdDictionary<any> {
        const currentUserCacheStore = this.store.getState().cache[userId];
        if (currentUserCacheStore) {
            return extend(true, {}, currentUserCacheStore[this.resolveCacheName()] ?? {});
        }
        return {};
    }

    private getSlimEntity(entity: Partial<T>) {
        let slimEntity: { [key: string]: unknown } = { id: entity.id };
        this.resolveCacheProperties().forEach(prop => {
            if (entity.hasOwnProperty(prop)) {
                slimEntity[prop] = extend(true, {}, { value: entity[prop] }).value;
            }
        });

        return slimEntity;
    }

    private readonly callsPreprocess = (searches, isSearch, resultsLimit?: number) => {
        let calls: any[] = [];

        searches.forEach(entity => {
            if (entity && Object.keys(entity).length > 0) {
                calls.push(["Get", {
                    typeName: this.resolvePropertyName(),
                    search: this.searchRewrite && !isSearch ? this.searchRewrite(entity) : entity,
                    ...(resultsLimit !== undefined && { resultsLimit })
                }]);
            }
        });

        return calls;
    };
}

export const makeUserCache = (store: DriveStore) =>
    new BaseCache<IUser>(store, "User", "user", ["name", "firstName", "lastName", "companyName", "companyAddress"]);

export const makeDeviceCache = (store: DriveStore) =>
    new BaseCache<Device | IDevice>(store, "Device", "device", ["name", "comment", "serialNumber", "deviceType", "licensePlate", "licenseState", "devicePlanBillingInfo", "deviceFlags",
        "vehicleIdentificationNumber", "activeTo", "activeFrom", "productId", "disableSleeperBerth", "autoHos", "tmpTrailerId"], device => typeof (device) === "string" ? { id: device } : device);

export const makeDefectListCache = (store: DriveStore) =>
    new BaseCache<IDefect>(store, "Defect", "defectList", ["id", "name", "children", "isDefectList", "severity", "assetType", "isArchived"]);

// MYG-148762: with DRIVE.ENABLE_TRAILER_DEVICE_LINK_API a trailer is identified by its own
// Device.id rather than the Trailer id held in Device.tmpTrailerId, so trailers are fetched as
// Devices and share the trailerDevice cache slot. Every call site keeps using makeTrailerCache
// and stays keyed on attachment.trailer.id, so no call site needs to know which API backs it.
const trailerDeviceCacheProperties: (keyof ITrailer & string)[] =
    ["name", "comment", "_isTemp", "tmpTrailerId", "licensePlate", "licenseState"];

export const makeTrailerCache = (store: DriveStore) =>
    new BaseCache<ITrailer>(
        store,
        () => usesTrailerDeviceIdentity(store) ? "Device" : "Trailer",
        () => usesTrailerDeviceIdentity(store) ? "trailerAsDevice" : "trailer",
        () => usesTrailerDeviceIdentity(store) ? trailerDeviceCacheProperties : ["name", "comment", "_isTemp"],
        trailer => { return { id: trailer.id }; }
    );

export const makeTrailerDeviceCache = (store: DriveStore) =>
    new BaseCache<Device | IDevice>(store, "Device", "trailerDevice", ["id", "name", "comment", "tmpTrailerId", "licensePlate", "licenseState"], device => { return { id: device.id }; });

/** The slot makeTrailerCache uses with the flag off, whatever the flag currently says. */
export const makeLegacyTrailerCache = (store: DriveStore) =>
    new BaseCache<ITrailer>(store, "Trailer", "trailer", ["name", "comment", "_isTemp"], trailer => { return { id: trailer.id }; });

/** The slot makeTrailerCache uses with the flag on, whatever the flag currently says. */
export const makeTrailerAsDeviceCache = (store: DriveStore) =>
    new BaseCache<ITrailer>(store, "Device", "trailerAsDevice", trailerDeviceCacheProperties, trailer => { return { id: trailer.id }; });

/**
 * The cache slot that actually holds trailer Devices for the current identity: the trailer cache's
 * own Device-backed slot under the flag, the dedicated trailerDevice slot otherwise. Readers that
 * ask "is this id a trailer device?" must go through this rather than picking a slot themselves.
 */
export const makeEffectiveTrailerDeviceCache = (store: DriveStore): BaseCache<Device | IDevice> =>
    usesTrailerDeviceIdentity(store)
        ? new BaseCache<Device | IDevice>(store, "Device", "trailerAsDevice", trailerDeviceCacheProperties as (keyof (Device | IDevice) & string)[], device => { return { id: device.id }; })
        : makeTrailerDeviceCache(store);

export const makeTrailerAttachmentCache = (store: DriveStore) =>
    new BaseCache<TrailerAttachment>(store, "TrailerAttachment", "trailerAttachment", ["activeFrom", "activeTo", "device", "trailer"]);

export const makeDeviceLinkCache = (store: DriveStore) =>
    new BaseCache<DeviceLink>(store, "DeviceLink", "deviceLink", ["fromDate", "toDate", "sourceDevice", "destinationDevice"]);